import { TagIcon } from "assets/svg";
import { inject, observer } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "src/components";
import { theme } from "src/config";
import { Space, Tag, User } from "src/models";
import { getRoutePath, MatchParams, Page } from "src/routes";
import { RoutingStore, SpaceStore, TagStore, UserStore, AuthStore } from "src/stores";
import { IData, SpaceType, UserType } from "src/types";
import styled from "styled-components";
import { AgendaCard, GroupCard, ProjectCard, TagCard, UserCard } from "./cards";
import { Section } from "./section";
import { ProjectTheme } from "src/types";
import toast from 'react-hot-toast';
import { Agenda } from "src/models/agenda";
import { AgendaStore } from "src/stores/agendaStore";
import { Badge } from "src/components/badge";
import { addDays } from 'date-fns';
import { sortAlph } from "src/util/util";


export interface IWorkSpaceProps {
  authStore:    AuthStore;
  userStore:    UserStore;
  spaceStore:   SpaceStore;
  tagStore:     TagStore;
  agendaStore:  AgendaStore;
  routingStore: RoutingStore;
  params?:      MatchParams;
}

const Container = styled.div`
  padding: 0 2rem;
`;

export const WorkSpaceSettings = inject("authStore", "userStore", "spaceStore", "tagStore", "routingStore", "agendaStore")(observer((props: IWorkSpaceProps): JSX.Element => {
  const { authStore, userStore, spaceStore, tagStore, routingStore, agendaStore } = props;
  const { t, i18n } = useTranslation();
  const ref = React.useRef<HTMLDivElement>();
  const [mounted, mount] = React.useState<boolean>(false);

  const projects: Space[]  = props.spaceStore.getItemsByType(SpaceType.Project);
  const users:    User[]   = userStore.getUsersBySpace(authStore.getCurWorkspaceID, [UserType.Default, UserType.Accountless]);
  const tags:     Tag[]    = React.useMemo(() => 
    sortAlph(tagStore.getItems, t => t.title?.toLocaleLowerCase())
  , [tagStore.getItems]);
  // const agendas:  Agenda[] = agendaStore.getItems;
  // const groups:   User[]   = userStore.getUsersBySpace(authStore.getCurWorkspaceID, [UserType.Group]);

  const curWorkspace: Space = spaceStore.getItemByID(authStore.getCurWorkspaceID);

  const closeCards = React.useCallback(() => {
    spaceStore.setActive(null);
    tagStore.setActive(null);
    userStore.setActive(null);
    agendaStore.setActive(null);
  }, []);

  React.useLayoutEffect(() => {
    if (Number(props.params.id) !== authStore.getCurWorkspaceID) {
      spaceStore.changeWorkspace(Number(props.params.id));
    }
    closeCards();
    mount(true);
  }, [])

  if (!curWorkspace || !mounted) return null;

  const currentUser = authStore.user;
  if( !currentUser.canEdit ) return null;

  return (
    <Container ref={ref}>
      {/* <Heading type="h3">{curWorkspace.title}</Heading> */}
      {/* Member Section */}
      <Section
        currentUser = {currentUser}
        data        = {users}
        space_id    = {authStore.getCurWorkspaceID}
        title       = {t("workspace_users")}
        newIcon     = {<Badge content={<Icon size="xsmall" color={theme.color.white} iconId="add" />}><Icon iconId="person" size="xxlarge" color={theme.color.grayDark} /></Badge>}
        newToolTip  = {t("tooltip_new_user")}
        Card        = {UserCard}
        onNew       = {() => {
          routingStore.push(getRoutePath(Page.UserInvite, { view: "workspace", id: authStore.getCurWorkspaceID }))
        }}
        // dataAlt       = {users}
        // newIconAlt    = {<Badge content={<Icon size="xsmall" color={theme.color.white} iconId="add" />}><Icon iconId="group" size="xxlarge" color={theme.color.grayDark} /></Badge>}
        // newToolTipAlt = {t("tooltip_new_group")}
        // CardAlt       = {GroupCard}
        // onNewAlt         = {async () => {
        //   try {
        //     await userStore.createGroup(t("new") + " " + t("group"), authStore.getCurWorkspaceID);
        //     toast.success(t('new_group_created'))
        //   } catch (error) {
        //     toast.error(t('failed_to_create_group'))
        //   }
        // }}
      />

      {/* Project Section */}
      <Section
        currentUser = {currentUser}
        data        = {projects}
        title       = {t("your_projects")}
        newToolTip  = {t("tooltip_new_project")}
        newIcon     = {<Badge content={<Icon size="xsmall" color={theme.color.white} iconId="add" />}><Icon size="xxlarge" iconId="project" color={theme.color.grayDark} /></Badge>}
        Card        = {ProjectCard}
        onNew       = {async () => {
          const project = Space.create({
            title:          t("new") + " " + t("project"),
            type:           SpaceType.Project,
            parent:         authStore.getCurWorkspaceID,
            workspace_id:   authStore.getCurWorkspaceID,
            theme:          ProjectTheme.Theme1,
            member_listing: 'name',
            start_date:     new Date(),
            end_date:       addDays(new Date(), 90),
          });
          try {
            await project.save();
            toast.success(`${t('new_project_created')}: ${project.title}`)
          } catch (error) {
            toast.error(`${t('failed_to_create_project')}`)
          }
        }}
      />
      {/* Tag Section */}
      <Section
        currentUser = {currentUser}
        data        = {tags}
        title       = {t("tag")}
        newToolTip  = {t("tooltip_new_tag")}
        newIcon     = {<Badge content={<Icon size="xsmall" color={theme.color.white} iconId="add" />}><TagIcon color={theme.color.grayDark} /></Badge>}
        Card        = {TagCard}
        onNew       = {async () => {
          const tag = Tag.create({
            title: t("new") + " " + t("tag"),
            workspace_id: authStore.getCurWorkspaceID,
          });
          try {
            await tag.save();
            toast.success(`${t('new_tag_created')}: ${tag.title}`)
          } catch (error) {
            toast.error(`${t('failed_to_create_tag')}`)
          }
        }}
      />

      {/* Agenda Section */}
      {/* <Section
        currentUser = {currentUser}
        newToolTip  = {t("tooltip_new_agenda")}
        data    = {agendas}
        title   = {t("agendas")}
        newIcon = {<Badge content={<Icon size="xsmall" color={theme.color.white} iconId="add"/>}><Icon iconId="list" color={theme.color.grayDark} size="xxlarge"/></Badge>}
        Card    = {AgendaCard}
        onNew   = {async () => {
          const agenda = Agenda.create({
            title: t("new") + " " + t("agenda"),
            steps: "",
            workspace_id: authStore.getCurWorkspaceID,
          });
          try {
            await agenda.save();
            toast.success(`${t('new_agenda_created')}: ${agenda.title}`)
          } catch(error) {
            toast.error(`${t('failed_to_create_agenda')}`)
          }
        }}
      /> */}
    </Container>
  );
}));
